// Mock data for Good Fruit App - Faith Community Platform
// NOTE: This is a startup. Names, churches, and quotes below are illustrative
// examples to show how the product can be used. They are NOT real partners.

export const brand = {
  name: "Good Fruit",
  tagline: "Where faith bears fruit \u2014 in your community, your finances, and your calling.",
  verse: "\u201cBy their fruit you will recognize them.\u201d \u2014 Matthew 7:16",
};

export const navLinks = [
  { label: "Platform", href: "/#platform" },
  { label: "About", href: "/about" },
  { label: "Finance Course", href: "/finance-course" },
  { label: "Funding", href: "/funding" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Stories", href: "/#stories" },
];

export const heroStats = [
  { value: "1", label: "Connected app", sub: "Replacing five fragmented tools" },
  { value: "8", label: "Finance modules", sub: "Built on biblical principles" },
  { value: "3", label: "Funding paths", sub: "From idea to expansion" },
  { value: "0", label: "Cost to start", sub: "Free to plant your Hub" },
];

// Honest framing for a new startup - "Built for" rather than "Trusted by"
export const builtFor = [
  "Local churches",
  "Small ministries",
  "Christian entrepreneurs",
  "Faith communities",
  "Bible studies",
];

export const organizations = [
  {
    id: 1,
    title: "Churches & Ministries",
    tagline: "A congregation that stays connected.",
    description:
      "From small parishes to growing ministry networks \u2014 a place to belong between Sundays. Sermons, events, and prayer woven into one connected home.",
    cta: "Learn more",
    image: "https://customer-assets.emergentagent.com/job_church-connect-hub-2/artifacts/nxx5g2o9_Screenshot_20260619_042823_Photos.jpg",
    featured: true,
  },
  {
    id: 2,
    title: "Christian Entrepreneurs",
    tagline: "Build a business that bears fruit.",
    description:
      "Connect faith and work. Tools, mentorship, and funding pathways for Kingdom-minded entrepreneurs ready to step out in faith.",
    cta: "Explore funding",
    image: "https://images.pexels.com/photos/7868979/pexels-photo-7868979.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: 3,
    title: "Faith Communities",
    tagline: "More than a Sunday gathering.",
    description:
      "Small groups, prayer chains, and outreach teams \u2014 with one connected home that meets people where they are, every day of the week.",
    cta: "See features",
    image: "https://customer-assets.emergentagent.com/job_church-connect-hub-2/artifacts/o9e9kvc8_Screenshot_20260619_043235_Chrome.jpg",
  },
  {
    id: 4,
    title: "Creators & Teachers",
    tagline: "Your voice. Your community. Built to last.",
    description:
      "Pastors, teachers, and Christian creators \u2014 turn followers into a flourishing community built around your work and your calling.",
    cta: "Learn more",
    image: "https://images.pexels.com/photos/8617834/pexels-photo-8617834.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

export const problems = [
  {
    icon: "Mail",
    title: "Emails that feel generic",
    body: "One-size-fits-all newsletters are easy to ignore. The people you most want to reach stop opening them first.",
  },
  {
    icon: "Calendar",
    title: "Events with no follow-through",
    body: "People show up once \u2014 then drift. Every event starts over from zero, and belonging never takes root.",
  },
  {
    icon: "Megaphone",
    title: "Messages that miss",
    body: "When everything goes through the same channel, the people who need to hear it most don't \u2014 and trust slowly erodes.",
  },
];

export const features = [
  {
    id: "newsletters",
    number: "01",
    tag: "Newsletters",
    title: "Messages that feel written just for them.",
    body: "Your Good Fruit Hub automatically builds personalised updates based on what each person follows. Not a broadcast \u2014 a conversation that begins with what they care about.",
    cta: "People who feel seen come back",
    image: "https://images.unsplash.com/photo-1513908957990-b2790723edbf",
  },
  {
    id: "events",
    number: "02",
    tag: "Events",
    title: "Events that start relationships.",
    body: "Your events live inside your Hub \u2014 where attendees stay connected, receive follow-ups, and keep engaging long after the day ends.",
    cta: "Attendance becomes belonging",
    image: "https://images.pexels.com/photos/20724348/pexels-photo-20724348.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "notifications",
    number: "03",
    tag: "Notifications",
    title: "Updates people choose to receive.",
    body: "Your Hub becomes a subscription experience. People opt into what matters to them \u2014 across web and app, entirely on their terms.",
    cta: "Less noise. Deeper engagement",
    image: "https://images.unsplash.com/photo-1761111566863-708fd5436f17?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
];

export const audiences = [
  "Churches",
  "Ministries",
  "Small Groups",
  "Christian Entrepreneurs",
  "Nonprofits",
  "Creators",
  "Pastors",
  "Teachers",
  "Outreach Teams",
  "Bible Studies",
];

// Financial Literacy Course
export const courseModules = [
  {
    id: "stewardship-101",
    number: "01",
    title: "Biblical Stewardship 101",
    verse: "1 Corinthians 4:2",
    verseText: "It is required of stewards that they be found faithful.",
    duration: "45 min",
    lessons: 6,
    summary:
      "Everything we have is entrusted to us by God. Learn what stewardship means and how to build a faithful financial mindset from the ground up.",
    practical: [
      "Build a personal money mission statement",
      "Audit where every dollar currently goes",
      "Set 3 stewardship goals for the next 90 days",
    ],
    color: "teal",
  },
  {
    id: "tithing-saving",
    number: "02",
    title: "Tithing & Saving with Purpose",
    verse: "Malachi 3:10",
    verseText: "Bring the whole tithe into the storehouse...",
    duration: "55 min",
    lessons: 7,
    summary:
      "Honor God first, then build a margin that lets you respond when He calls. A practical framework for giving and saving simultaneously.",
    practical: [
      "Set up automatic tithing & savings transfers",
      "Open a starter emergency fund ($1,000 target)",
      "Build a 50/30/20 budget rooted in giving",
    ],
    color: "teal",
  },
  {
    id: "debt-freedom",
    number: "03",
    title: "Debt Freedom Pathway",
    verse: "Proverbs 22:7",
    verseText: "The borrower is slave to the lender.",
    duration: "60 min",
    lessons: 8,
    summary:
      "Step-by-step plan to break free from consumer debt without shame \u2014 with a faith-based lens on contentment, repentance, and restoration.",
    practical: [
      "List every debt with rate, balance, minimum payment",
      "Choose snowball or avalanche strategy",
      "Cut 3 expenses to fund your payoff plan",
    ],
    color: "black",
  },
  {
    id: "credit-education",
    number: "04",
    title: "Credit, Wisely Used",
    verse: "Proverbs 3:5-6",
    verseText: "Trust in the Lord with all your heart...",
    duration: "50 min",
    lessons: 6,
    summary:
      "Understand how credit works, how to repair it, and how to leverage it for Kingdom purposes \u2014 not consumer traps. Part of Credit Mastery (Premium).",
    practical: [
      "Pull your free credit reports & dispute errors",
      "Understand the 5 factors of your FICO score",
      "Build credit responsibly with secured cards & utilization rules",
    ],
    color: "teal",
    premium: true,
  },
  {
    id: "generosity",
    number: "05",
    title: "The Generosity Mindset",
    verse: "2 Corinthians 9:7",
    verseText: "God loves a cheerful giver.",
    duration: "40 min",
    lessons: 5,
    summary:
      "Move from scarcity thinking to open-handed generosity. How giving multiplies, both spiritually and practically.",
    practical: [
      "Identify a cause to support beyond your tithe",
      "Plan one anonymous act of generosity this month",
      "Teach someone the basics of biblical money",
    ],
    color: "black",
  },
  {
    id: "entrepreneurship-intro",
    number: "06",
    title: "Intro to Entrepreneurship",
    verse: "Proverbs 16:3",
    verseText: "Commit to the Lord whatever you do, and He will establish your plans.",
    duration: "70 min",
    lessons: 9,
    summary:
      "Discover whether God is calling you to start something. From idea to first customer \u2014 the foundational mindset and steps every Christian founder needs.",
    practical: [
      "Discern your calling vs. ambition (workbook)",
      "Validate your idea with 5 conversations",
      "Draft a one-page lean canvas",
    ],
    color: "teal",
  },
  {
    id: "faith-business",
    number: "07",
    title: "How Faith Connects to Starting a Business",
    verse: "Colossians 3:23",
    verseText: "Whatever you do, work at it with all your heart, as working for the Lord.",
    duration: "65 min",
    lessons: 8,
    summary:
      "Your business is a mission field. Learn how to build company values, hiring, ethics, and customer love rooted in scripture.",
    practical: [
      "Write your business' biblical core values",
      "Plan how profit will fund Kingdom impact",
      "Set boundaries that honor Sabbath & family",
    ],
    color: "black",
  },
  {
    id: "wealth-legacy",
    number: "08",
    title: "Wealth, Legacy & Generations",
    verse: "Proverbs 13:22",
    verseText: "A good person leaves an inheritance for their children's children.",
    duration: "55 min",
    lessons: 7,
    summary:
      "Build wealth that outlives you \u2014 with wills, generational giving, family money meetings, and a vision beyond yourself.",
    practical: [
      "Draft or update your will & beneficiaries",
      "Hold a family money meeting this month",
      "Set a generational generosity goal",
    ],
    color: "teal",
  },
];

export const courseStats = [
  { value: "8", label: "Modules" },
  { value: "56+", label: "Lessons" },
  { value: "Self-paced", label: "Format" },
  { value: "Free", label: "For members" },
];

// Premium subscription \u2014 Good Fruit Credit Mastery
export const STRIPE_CHECKOUT_URL =
  "https://buy.stripe.com/28E7sM1wH5LEcgLaPVawo00";

// Founders' Circle promo code \u2014 created in Stripe Dashboard.
// Code value (case-sensitive) must match exactly what was set in Stripe.
export const FOUNDERS_PROMO_CODE = "Founders12";
export const FOUNDERS_CHECKOUT_URL = `${STRIPE_CHECKOUT_URL}?prefilled_promo_code=${FOUNDERS_PROMO_CODE}`;
export const FOUNDERS_SEAT_COUNT = 12;

export const subscription = {
  name: "Good Fruit Credit Mastery",
  tagline: "Premium credit education + real tools to act on it.",
  price: "29.95",
  cadence: "month",
  highlight: "Most actionable",
  description:
    "Credit is one of the biggest barriers to flourishing for believers. Credit Mastery unlocks the full credit curriculum, monthly accountability, and partner tools to monitor and repair your credit \u2014 all in one membership.",
  benefits: [
    "Full Credit, Wisely Used curriculum (6 lessons + workbook)",
    "Monthly group accountability call",
    "Dispute-letter templates & credit-plan worksheets",
    "Premium-only Q&A with the founder",
    "Partner access: IdentityIQ credit monitoring",
    "Partner access: CreditScoreIQ DIY credit repair",
    "Cancel anytime \u2014 no contracts",
  ],
  cta: "Start Credit Mastery",
};

export const partnerTools = [
  {
    id: "identityiq",
    name: "IdentityIQ",
    category: "Credit monitoring & identity protection",
    summary:
      "Real-time monitoring of all 3 credit bureaus, identity-theft protection, and instant alerts \u2014 included for Credit Mastery members.",
    bullets: [
      "3-bureau credit monitoring",
      "Daily score updates",
      "Identity-theft insurance",
      "Dark-web surveillance",
    ],
    url: "https://www.identityiq.com/sc-securepreferred.aspx?offercode=431297L8",
    accent: "teal",
  },
  {
    id: "creditscoreiq",
    name: "CreditScoreIQ",
    category: "DIY credit repair toolkit",
    summary:
      "Step-by-step DIY credit repair platform with dispute letters, tradeline tracking, and progress dashboards \u2014 the practical companion to the curriculum.",
    bullets: [
      "Guided DIY dispute process",
      "Letter templates & tracking",
      "Tradeline & utilization tools",
      "Progress dashboard",
    ],
    url: "https://www.creditscoreiq.com/lander/bus?offercode=435404P8",
    accent: "black",
  },
];

// Funding paths
export const fundingPaths = [
  {
    id: "leap-of-faith",
    icon: "Sprout",
    badge: "Most popular",
    title: "Leap of Faith Funding",
    audience: "Aspiring Christian entrepreneurs",
    range: "$1,000 \u2013 $25,000",
    summary:
      "For believers who feel called to start \u2014 but don't yet have collateral, traction, or a track record. Designed to get the first version of your idea into the world.",
    features: [
      "Idea-stage friendly \u2014 no revenue required",
      "Mentorship pairing with a Christian founder",
      "Faith-based covenant agreement",
      "Funds released in milestones",
    ],
  },
  {
    id: "start-faith-org",
    icon: "Heart",
    title: "Start a Faith-Based Org",
    audience: "Church plants, ministries, and 501(c)(3)s",
    range: "$5,000 \u2013 $75,000",
    summary:
      "Launch a new faith-based organization with seed capital, formation guidance, and a community of fellow founders walking the same road.",
    features: [
      "Nonprofit formation support included",
      "Mission alignment review",
      "First-year operating runway",
      "Connection to denominational partners",
    ],
  },
  {
    id: "expand-existing",
    icon: "TrendingUp",
    title: "Expand an Existing One",
    audience: "Established faith-based businesses & ministries",
    range: "$25,000 \u2013 $250,000",
    summary:
      "Already faithful with little? It's time to grow. Funding for proven, mission-aligned organizations ready to scale impact and operations.",
    features: [
      "Growth capital for hiring, tech, expansion",
      "Stewardship-aligned terms",
      "Quarterly Kingdom-impact reporting",
      "Optional revenue-share repayment",
    ],
  },
];

export const fundingProcess = [
  {
    step: "01",
    title: "Apply through LMI",
    body: "Click the funding button and complete a short intake survey at LMI Marketing & Business Solutions Group \u2014 our funding partner.",
  },
  {
    step: "02",
    title: "Pre-qualification review",
    body: "Our team reviews your survey, prays over your call, and contacts you within 5 business days with next steps.",
  },
  {
    step: "03",
    title: "Discovery conversation",
    body: "A 30-minute call to understand your mission, your numbers, and the fruit you're trying to bear.",
  },
  {
    step: "04",
    title: "Funding decision",
    body: "Approved applicants receive a covenant offer. You bring the calling \u2014 we bring the capital and the community.",
  },
];

// LMI partner site is launching shortly. Until it goes live, the funding flow
// is routed through an internal interim form at /apply. Once LMI is live,
// change LMI_FUNDING_URL back to its real URL and the funding buttons will
// auto-update.
export const LMI_FUNDING_URL = "/apply";
export const LMI_EXTERNAL = false; // set true once lmimarketing.com is live

export const pricingFragmented = [
  { label: "Website", price: "$30/mo" },
  { label: "Email + newsletters", price: "$50/mo" },
  { label: "Event platform", price: "$75/mo" },
  { label: "Community tool", price: "$40/mo" },
  { label: "Membership system", price: "$50/mo" },
];

export const pricingAllInOne = [
  "Personalised newsletters",
  "Events + follow-up",
  "Community channels",
  "Biblical finance course",
  "Push notifications + app",
];

// Illustrative use-cases (clearly labelled in the UI as "How it could feel" examples)
export const testimonials = [
  {
    id: 1,
    quote:
      "Our congregation finally feels connected between Sundays. The Hub is one place for prayer, sermons, and serving \u2014 and people actually use it.",
    author: "A Pastor",
    role: "How a small church might feel",
    duration: "0:34",
  },
  {
    id: 2,
    quote:
      "Leap of Faith Funding is the first time I've seen capital paired with calling. It treats the founder like a person, not a credit score.",
    author: "An Aspiring Founder",
    role: "How an entrepreneur might feel",
    duration: "1:24",
  },
  {
    id: 3,
    quote:
      "The biblical finance course gave our small group a real plan \u2014 not just principles. We're talking about money the way scripture does.",
    author: "A Small Group Leader",
    role: "How a ministry might feel",
    duration: "0:27",
  },
  {
    id: 4,
    quote:
      "One Hub replaced our website, our email tool, and our event platform. Volunteers stopped falling through the cracks.",
    author: "A Ministry Lead",
    role: "How a community might feel",
    duration: "0:45",
  },
];

export const footerLinks = {
  Platform: ["Hubs", "Newsletters", "Events", "Notifications", "Membership"],
  Grow: ["Finance Course", "Funding", "Mentorship", "Partners"],
  Solutions: ["Churches", "Ministries", "Entrepreneurs", "Creators", "Nonprofits"],
  Company: ["About", "Founder", "Contact", "Privacy", "Terms"],
};

// Founder / About page
export const FOUNDER_PHOTO =
  "https://customer-assets.emergentagent.com/job_church-connect-hub-2/artifacts/hgaxko7q_20260425_154939.jpg";

export const FOUNDER_SIGNATURE =
  "https://customer-assets.emergentagent.com/job_church-connect-hub-2/artifacts/bqr2jy6m_TH_20260428_144910_0000.png";

export const founder = {
  name: "Min. Tyree A. Huddleston III",
  shortName: "Tyree A. Huddleston III",
  title: "Founder & CEO, Good Fruit App",
  shortBio:
    "Licensed and ordained minister, two-time published author, and Kingdom-minded entrepreneur on a mission to be good fruit and a disciple for Christ \u2014 helping His people flourish spiritually and financially.",
  credentials: [
    {
      icon: "Cross",
      label: "Licensed & ordained minister",
      sub: "Called to shepherd, teach, and disciple",
    },
    {
      icon: "BookOpen",
      label: "Two-time published author",
      sub: "Focused on financial literacy & Kingdom growth",
    },
    {
      icon: "Sprout",
      label: "Faith-driven entrepreneur",
      sub: "Building tools that grow His Kingdom",
    },
    {
      icon: "Mic",
      label: "Speaker & teacher",
      sub: "Bridging scripture and stewardship",
    },
  ],
  values: [
    {
      title: "Christ at the center",
      body: "Good Fruit is openly and unapologetically a Christian company. Every feature, every dollar, every decision is filtered through scripture and surrendered to Him.",
      verse: "Colossians 1:17",
    },
    {
      title: "Stewardship over hustle",
      body: "We treat money the way the Bible does \u2014 as something entrusted, not owned. Our finance course exists to free people from debt and into generosity.",
      verse: "1 Corinthians 4:2",
    },
    {
      title: "Capital with calling",
      body: "Through Leap of Faith Funding, we put real capital behind Christian entrepreneurs who feel called \u2014 because faith without practical support too often dies on the vine.",
      verse: "James 2:17",
    },
    {
      title: "Bear good fruit",
      body: "We measure success not in users, but in lives changed, debts paid off, ministries launched, and disciples made. By the fruit, you will know us.",
      verse: "Matthew 7:16",
    },
  ],
};

// Books authored by the founder \u2014 available on Amazon
export const books = [
  {
    id: "financial-awakening",
    series: "Dope Boy Fresh But Dead Broke",
    title: "A Young Man's Guide to Financial Awakening",
    description:
      "The first volume of the Dope Boy Fresh But Dead Broke series \u2014 a wake-up call for young men trapped between the lure of fast money and the freedom of true wealth. Real stories, scripture, and practical steps to break the cycle.",
    accent: "from-teal-500 to-teal-700",
    amazonUrl: "https://www.amazon.com/s?k=Dope+Boy+Fresh+But+Dead+Broke+Financial+Awakening+Tyree+Huddleston",
    tag: "Volume 1",
  },
  {
    id: "abundant-thinking",
    series: "Dope Boy Fresh But Dead Broke",
    title: "A Young Man's Guide to Abundant Thinking",
    description:
      "Volume two of the series \u2014 the mindset shift that turns financial awakening into a life of abundance. Renew your mind, redirect your money, and step into the calling God placed on your life.",
    accent: "from-teal-600 to-emerald-800",
    amazonUrl: "https://www.amazon.com/s?k=Dope+Boy+Fresh+But+Dead+Broke+Abundant+Thinking+Tyree+Huddleston",
    tag: "Volume 2",
  },
];
